/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { useSelector, useDispatch } from 'react-redux';
import { getColor } from '@panel/commons/core/themev3';
import { select } from '@panel/commons/core/themev2';
import Icon from '../../../commons/UploadIcon.js';
import {
  backendForDND,
  MAX_FILES,
  createPicture,
  dropZoneConfig,
  keysPlansErrors,
  resolveImageDimensions,
  validateImageErrors,
  isValidImageFormat,
  normalizeFileWithMimeType
} from './helpers';
import { ModuleWrapper } from '../moduleWrapper';
import { ItemList } from './itemlist';
import {
  SubtitleHelper,
  SectionContainer,
  FilesContainer,
  DropzoneContainer,
  CollapseContainer,
  CollapseWrapper
} from './styles';
import {
  getPlans,
  addMultimedia,
  updateMultimedia,
  deleteMultimedia,
  moveMultimedia,
  MultimediaType,
  MultimediaCollection,
  addErrorMultimedia
} from '../../../../ducks/multimedia';
import ToggleButton from '../../../commons/ToggleButton';
import useSectionsCollapse from '../../../../hooks/useSectionsCollapse.js';

const PlansSection = ({ device, showErrorSnackbar, postingId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { plans } = useSelector(getPlans);
  const isDisabled = plans.length >= MAX_FILES;
  const { registerRef, getMaxHeight, isCollapsed, setCollapsed } = useSectionsCollapse({
    visibleItems: 5,
    itemSelector: '.plans-item',
    dependencies: [plans.length]
  });

  const fileDispatcher = (files = []) => {
    // Calculamos cuántos archivos podemos aceptar, restando los que ya tiene cargado el usuario
    const availableFiles = MAX_FILES - plans.length;

    // Si no hay espacio disponible, mostramos mensaje
    if (availableFiles <= 0) {
      showErrorSnackbar('ImageLimitMax', t('professionalPubli.multimedia.plans.errorLimitMax'));
      return; // Salimos de la función para evitar añadir más archivos
    }
    // Mostramos mensaje si se rechazaron archivos por límite excedido
    if (files.length > availableFiles) {
      showErrorSnackbar('LimitMaxError', t('professionalPubli.multimedia.plans.errorLimitMax'));
    }
    // Mostramos mensaje si algún archivo tiene errores
    setTimeout(() => {
      if (files.some(file => file.error)) {
        showErrorSnackbar('ValidationError', t('professionalPubli.multimedia.plans.hasError'));
      }
      // Pequeño retraso para asegurar que se procese el estado correctamente y se muestre el segundo snackbar
    }, 100);

    // Tomamos solo la cantidad de archivos que podemos aceptar
    const filesToProcess = files.slice(0, availableFiles);
    // Añadimos los archivos aceptados
    filesToProcess.forEach(file => {
      const picture = createPicture(file, MultimediaType.PLAN);
      dispatch(addMultimedia(picture, MultimediaCollection.PLAN));
    });
  };

  const onDrop = async acceptedFiles => {
    if (acceptedFiles.length === 0) return;
    // Validamos y obtenenos dimensiones de cada archivo
    const processedFiles = await Promise.all(
      acceptedFiles.map(async file => {
        // Normalizamos primero el type del file:
        const normalizedFile = normalizeFileWithMimeType(file);
        // Verificamos luego el formato del file:
        if (!isValidImageFormat(normalizedFile)) {
          return { ...normalizedFile, error: keysPlansErrors.format };
        }
        // Obtenemos las dimensiones
        const result = await resolveImageDimensions(normalizedFile);
        // Validamos las restricciones de dimensiones y peso:
        return validateImageErrors(result.file, result.dimensions, keysPlansErrors);
      })
    );
    // Enviamos los archivos procesados al dispatcher
    fileDispatcher(processedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropZoneConfig,
    onDrop
  });

  const onDelete = id => {
    dispatch(deleteMultimedia(id, MultimediaCollection.PLAN));
  };

  const onUpdate = multimedia => {
    dispatch(updateMultimedia(multimedia, MultimediaCollection.PLAN));
  };

  const onMove = (dragIndex, hoverIndex) => {
    dispatch(moveMultimedia(dragIndex, hoverIndex, MultimediaCollection.PLAN));
  };

  const onError = (id, keyError = 'errorGeneric') => {
    dispatch(addErrorMultimedia(id, MultimediaCollection.PLAN, `professionalPubli.multimedia.plans.${keyError}`));
    showErrorSnackbar('ValidationError', t('professionalPubli.multimedia.plans.hasError'));
  };

  return (
    <ModuleWrapper padding={plans.length > 5 ? '0px 0px 8px' : '0'}>
      <SubtitleHelper data-publi='multimedia-plans-subtitle'>
        {t('professionalPubli.multimedia.plans.subtitle')}
      </SubtitleHelper>
      <SectionContainer data-publi='multimedia-plans-sectionContainer'>
        <DndProvider backend={backendForDND}>
          <CollapseWrapper data-publi='multimedia-plans-collapsableWrapper'>
            <CollapseContainer
              data-publi='multimedia-plans-collapsableContainer'
              device={device}
              ref={registerRef}
              maxHeight={getMaxHeight()}
              hasToggleButton={plans.length > 5}
            >
              <FilesContainer data-publi='multimedia-plans-fileContainer' hasItems={plans.length > 0} device={device}>
                <DropzoneContainer
                  data-publi='multimedia-plans-dropzone'
                  {...getRootProps()}
                  disabled={isDisabled}
                  hasItems={plans.length > 0}
                  device={device}
                >
                  <input data-publi='multimedia-plans-input' {...getInputProps()} disabled={isDisabled} />
                  {!isDragActive && (
                    <>
                      <Icon
                        width={32}
                        height={32}
                        fill={isDisabled ? select('midNeutralGray') : getColor('primary')}
                        viewBox='0 0 32 32'
                      />
                      <span data-publi='multimedia-plans-input-label'>
                        {t('professionalPubli.multimedia.plans.add')}
                      </span>
                    </>
                  )}
                </DropzoneContainer>
                <ItemList
                  data-publi='multimedia-plans-itemlist'
                  items={plans}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onMove={onMove}
                  onError={onError}
                  photoType='plans'
                  postingId={postingId}
                  itemSelector='plans-item'
                  device={device}
                />
              </FilesContainer>
            </CollapseContainer>
            {plans.length > 5 && (
              <ToggleButton
                dataPubli='multimedia-plans-showMore'
                device={device}
                title={t(`professionalPubli.multimedia.plans.${isCollapsed() ? 'showMore' : 'showLess'}`)}
                active={!isCollapsed()}
                setActive={() => setCollapsed()}
                marginRight='13px'
                marginTop='20px'
                widthIcon='10px'
                heightIcon='6.1px'
              />
            )}
          </CollapseWrapper>
        </DndProvider>
      </SectionContainer>
    </ModuleWrapper>
  );
};

export { PlansSection };
